import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Image as ImageIcon, Upload, X } from "lucide-react";
import EditorShell from "../EditorShell";
import { heroImageSchema, SECTION_DEFAULTS } from "../schemas";
import {
  getHeroImageUploadUrl,
  uploadLogoToS3,
} from "../../../../../../Services/adminCorporates";
import { Button } from "../../../../../../ui/button";
import { Label } from "../../../../../../ui/label";

const ALLOWED_EXT = ["png", "jpg", "jpeg"];
const MAX_BYTES = 4 * 1024 * 1024;

const HeroImageField = ({ form }) => {
  const { id: corporateId } = useParams();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const imageUrl = form.watch("image_url");
  const imageKey = form.watch("image_s3_key");

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!ext || !ALLOWED_EXT.includes(ext)) {
      setError("Image must be a PNG, JPG, or JPEG file.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("Image is larger than 4 MB. Please trim and try again.");
      e.target.value = "";
      return;
    }

    setUploading(true);
    try {
      const presigned = await getHeroImageUploadUrl(corporateId, ext);
      if (!presigned?.upload_url || !presigned?.s3_key) {
        throw new Error("Server did not return an upload URL.");
      }
      await uploadLogoToS3(presigned.upload_url, file, ext);
      form.setValue("image_s3_key", presigned.s3_key, { shouldDirty: true });
      // Optimistic local URL so the preview updates instantly. Save+refetch
      // replaces it with the backend's presigned display URL.
      form.setValue("image_url", URL.createObjectURL(file));
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Image upload failed. Please try again."
      );
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleRemove = () => {
    form.setValue("image_s3_key", "", { shouldDirty: true });
    form.setValue("image_url", "");
    setError(null);
  };

  return (
    <div className="grid gap-3">
      <Label>Hero image</Label>
      <p className="text-xs text-muted-foreground">
        Optional full-width banner above the page. PNG / JPG, max 4 MB.
        Leave empty to hide the banner entirely.
      </p>
      <div className="rounded-md border bg-muted/30 overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="Hero banner"
            className="w-full max-h-[280px] object-cover"
          />
        ) : (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            <ImageIcon className="h-8 w-8" />
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          <Upload className="h-3.5 w-3.5 mr-1.5" />
          {uploading
            ? "Uploading…"
            : imageKey
            ? "Replace image"
            : "Upload image"}
        </Button>
        {imageKey && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            disabled={uploading}
          >
            <X className="h-3.5 w-3.5 mr-1.5" />
            Remove
          </Button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".png,.jpg,.jpeg,image/png,image/jpeg"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
      {error && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

// Strip read-only enrichment keys before save. Backend recomputes
// `image_url` from `image_s3_key` on read.
const stripReadOnly = (values) => {
  const { image_url: _ignored, ...rest } = values;
  return {
    ...rest,
    image_s3_key: rest.image_s3_key || null,
  };
};

const HeroImageEditor = ({ section, onSaved, onLiveChange }) => (
  <EditorShell
    sectionKey="hero_image"
    schema={heroImageSchema}
    defaults={SECTION_DEFAULTS.hero_image}
    section={section}
    onSaved={onSaved}
    onLiveChange={onLiveChange}
    transformBeforeSave={stripReadOnly}
  >
    {(form) => <HeroImageField form={form} />}
  </EditorShell>
);

export default HeroImageEditor;
